import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { InviteUserFormSchema } from '../../../schemas';
import OrganizationRole from '../../UI/OrganizationRole';
import { PrimaryButton, SecondaryButton } from "../../UI";
import { useUserContext } from '../../../contexts/UserContext';
import { inviteUser } from '../../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../../constants/analyticsEvents';
import Loader from '../Loader/Loader';
import { FormikInput } from '../Formik';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import { toast } from 'react-toastify';

const InviteUserForm = ({ startCountdown, setShowInviteUserModal, refreshData }) => {
    const { roles, userData } = useUserContext();
    const { content } = useContentfulContext();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const data = {
                email: values.email,
                name: values.name,
                organizationId: userData.selectedOrganization.org_token,
                organizationName: userData.selectedOrganization.name,
                roleId: roles.find((x) => x.name === values.organizationRole)?.id,
            };
            const result = await inviteUser(userData?.token, data, {
                event_name: ANALYTICS_EVENTS.INVITE_NEW_USER,
                metadata: { invitee_email: data.email }
            });
            if (result?.data?.message && result.data.message.includes('User already registered')) {
                toast.success(result.data.message);
                setShowInviteUserModal(false);
            } else {
                refreshData?.();
                startCountdown?.();
            }
        } catch (error) {
            let errorMessage = error?.response?.data?.message;
            if (Array.isArray(error?.response?.data)) {
                errorMessage = error.response.data.map(e => `${e.email}: ${e.reason || 'Failed to invite'}`).join('\n');
            }
            if (!errorMessage) errorMessage = 'Failed to invite user.';
            console.log('error in InviteUserForm: ', error);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <Loader />}
            <Formik
                initialValues={{
                    email: '',
                    name: '',
                    organizationRole: ''
                }}
                validationSchema={() => InviteUserFormSchema(content.INVITE_USER_MODAL)}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, setFieldValue, setFieldTouched, values }) => (
                    <Form className="space-y-4 md:space-y-6" action="#" id="invite-user-form-id">
                        <FormikInput
                            type="email"
                            id="email"
                            name="email"
                            label={content.INVITE_USER_MODAL.formLabels.email}
                            placeholder="user@company.com"
                        />
                        <FormikInput
                            type="text"
                            id="name"
                            name="name"
                            label={content.INVITE_USER_MODAL.formLabels.name}
                            placeholder="Full name"
                        />
                        <div className="mb-4 text-left">
                            <label htmlFor="organization-role" className="stride-form-label">
                                {content.INVITE_USER_MODAL.instruction_text.org_title}
                            </label>
                            <p className="text-xs text-stride-muted mb-2">
                                {content.INVITE_USER_MODAL.instruction_text.org_description}
                            </p>
                            <OrganizationRole
                                id="organization-role"
                                organizationRole={values.organizationRole}
                                roles={roles}
                                error={(touched.organizationRole && errors.organizationRole) ? errors.organizationRole : undefined}
                                callback={(role) => {
                                    setFieldValue('organizationRole', role, true);
                                    setFieldTouched('organizationRole', true, false);
                                }}
                            />
                        </div>
                        <div className="flex items-center justify-end space-x-4">
                            <SecondaryButton handleClick={() => {
                                setShowInviteUserModal(false);
                            }}>
                                {content.INVITE_USER_MODAL.formLabels.cancel_cta_text}
                            </SecondaryButton>
                            <PrimaryButton id="send-invite-user-button-id" type="submit">
                                {content.INVITE_USER_MODAL.formLabels.save_cta_text}
                            </PrimaryButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default InviteUserForm;
